import { useEffect, useState } from 'react';

const API = `${window.location.protocol}//${window.location.hostname}/api`;
function api(path, opts = {}) {
  const token = localStorage.getItem('token');
  return fetch(`${API}${path}`, {
    ...opts,
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}`, ...opts.headers },
  }).then(async r => {
    const d = await r.json().catch(() => ({}));
    if (!r.ok) throw new Error(d.message || d.error || `HTTP ${r.status}`);
    return d;
  });
}

const GATE_FT = 500;
function haversineFt(la1, lo1, la2, lo2) {
  const R = 20902231; // earth radius (ft)
  const toRad = d => d * Math.PI / 180;
  const dLa = toRad(la2 - la1), dLo = toRad(lo2 - lo1);
  const a = Math.sin(dLa / 2) ** 2 + Math.cos(toRad(la1)) * Math.cos(toRad(la2)) * Math.sin(dLo / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

const wrap = { backgroundColor: '#0b1220', border: '1px solid #1e3a5f', borderRadius: 10, padding: 16, marginBottom: 16 };
const head = { color: '#93c5fd', fontSize: 11, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 6 };
const msg = { color: '#e2e8f0', fontSize: 14, fontWeight: 700, marginBottom: 4 };
const sub = { color: '#64748b', fontSize: 12, marginBottom: 4 };
const primary = (bg = '#2563eb', fg = 'white') => ({ width: '100%', padding: 12, backgroundColor: bg, color: fg, border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 800, fontSize: 14, marginTop: 8 });
const ghost = { width: '100%', padding: 10, backgroundColor: 'transparent', color: '#94a3b8', border: '1px solid #334155', borderRadius: 8, cursor: 'pointer', fontWeight: 600, fontSize: 13, marginTop: 8 };

export default function InstallFlow({ item, onRefresh, onBeginInstall, onFieldComplete, onSubmitFMP }) {
  const [me, setMe] = useState(null);
  const [openEntry, setOpenEntry] = useState(null);
  const [wizard, setWizard] = useState(null); // material | drive | bench | verify
  const [busy, setBusy] = useState(false);
  const [matStatus, setMatStatus] = useState({ hasMaterial: false, checkedOut: false });
  const [pendingDrive, setPendingDrive] = useState(null);

  const step = item.install_step || null;
  const openType = openEntry && String(openEntry.work_item_id) === String(item.id) ? openEntry.activity_type : null;

  useEffect(() => { api('/time-entries/open').then(setOpenEntry).catch(() => {}); }, []);
  useEffect(() => { api('/auth/me').then(setMe).catch(() => {}); }, []);
  useEffect(() => {
    Promise.all([
      api(`/materials/work-item/${item.id}`).catch(() => ({ shipments: [] })),
      api(`/materials/checkout/work-item/${item.id}`).catch(() => ({ checkout: null })),
    ]).then(([m, c]) => setMatStatus({ hasMaterial: (m.shipments || []).length > 0, checkedOut: !!c.checkout }));
  }, [item.id]);

  const setStep = async (s) => {
    setBusy(true);
    try { await api(`/work-items/${item.id}/install-step`, { method: 'PATCH', body: JSON.stringify({ step: s }) }); onRefresh(); }
    finally { setBusy(false); }
  };
  const startTimer = async (activity, notes) => {
    try { const e = await api('/time-entries/start', { method: 'POST', body: JSON.stringify({ work_item_id: item.id, activity_type: activity, notes }) }); setOpenEntry(e); }
    catch (err) { alert(err.message); }
  };
  const stopTimer = async () => {
    if (!openEntry) return;
    try { await api(`/time-entries/${openEntry.id}/stop`, { method: 'PATCH', body: JSON.stringify({}) }); setOpenEntry(null); } catch {}
  };

  const siteUrl = item.latitude && item.longitude
    ? `https://maps.google.com/?q=${item.latitude},${item.longitude}`
    : item.location_name ? `https://maps.google.com/?q=${encodeURIComponent(item.location_name)}` : null;
  const benchUrl = me?.bench_lat && me?.bench_lon
    ? `https://maps.google.com/?q=${me.bench_lat},${me.bench_lon}`
    : me?.bench_location_name ? `https://maps.google.com/?q=${encodeURIComponent(me.bench_location_name)}` : null;

  // Geofence: while driving to site, auto-stop the clock + advance to "arrived"
  const drivingToSite = step === 'drive' && openType === 'drive';
  useEffect(() => {
    if (!drivingToSite || !item.latitude || !item.longitude || !navigator.geolocation) return;
    const id = navigator.geolocation.watchPosition(pos => {
      const d = haversineFt(pos.coords.latitude, pos.coords.longitude, item.latitude, item.longitude);
      if (d <= GATE_FT) { navigator.geolocation.clearWatch(id); stopTimer(); setStep('arrived'); }
    }, null, { enableHighAccuracy: true, timeout: 30000 });
    return () => navigator.geolocation.clearWatch(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [drivingToSite, item.latitude, item.longitude]);

  // ── actions ──
  const recheckMaterial = async () => {
    const c = await api(`/materials/checkout/work-item/${item.id}`).catch(() => ({ checkout: null }));
    const co = !!c.checkout;
    setMatStatus(s => ({ ...s, checkedOut: co }));
    return co;
  };
  const doDriveToSite = async () => { if (siteUrl) window.open(siteUrl, '_blank'); await startTimer('drive', 'Drive to site'); await setStep('drive'); setWizard(null); };
  const doBenchToSite = async () => { await stopTimer(); if (siteUrl) window.open(siteUrl, '_blank'); await startTimer('drive', 'Drive bench to site'); await setStep('drive'); setWizard(null); };
  const runDrive = (kind) => kind === 'bench' ? doBenchToSite() : doDriveToSite();
  const guardDrive = (kind) => { if (matStatus.hasMaterial && !matStatus.checkedOut) { setPendingDrive(kind); setWizard('verify'); } else runDrive(kind); };
  const arrivedAtSite = async () => { await stopTimer(); await setStep('arrived'); };
  const begin = async () => { await stopTimer(); await setStep('installing'); onBeginInstall(); };
  const startBench = async (withDir) => { if (withDir && benchUrl) { window.open(benchUrl, '_blank'); await startTimer('drive', 'Drive to bench'); } else { await startTimer('bench', 'Bench work'); } await setStep('bench'); setWizard(null); };
  const arrivedAtBench = async () => { await stopTimer(); await startTimer('bench', 'Bench work'); };

  // Field install complete — physical install done, submit the FMP next
  if (item.status === 'field_install_complete') {
    return (
      <div style={wrap}>
        <div style={head}>Field Flow</div>
        <div style={msg}>✅ Field Install Complete</div>
        <div style={sub}>Physical install is done. Submit your FMP to hand off to the RPM.</div>
        <button style={primary('#c2410c', 'white')} onClick={() => onSubmitFMP && onSubmitFMP()}>📄 Submit FMP</button>
      </div>
    );
  }

  // Field complete — FMP submitted, handed to the RPM for design review + close
  if (['fmp_pending', 'design_review'].includes(item.status)) {
    return (
      <div style={wrap}>
        <div style={head}>Field Flow</div>
        <div style={msg}>✅ Field Complete</div>
        <div style={sub}>FMP submitted. Handed to the RPM for design review and close — nothing more for you here.</div>
      </div>
    );
  }

  // ── wizard questions ──
  if (wizard === 'material') {
    return (
      <div style={wrap}>
        <div style={head}>Start Install</div>
        <div style={msg}>Did you pick up the material for this job?</div>
        <button style={primary('#166534', '#dcfce7')} onClick={() => setWizard('drive')}>Yes — I have it</button>
        <button style={ghost} onClick={() => { setWizard(null); setStep('pickup'); }}>No — not yet</button>
      </div>
    );
  }
  if (wizard === 'drive') {
    return (
      <div style={wrap}>
        <div style={head}>Start Install</div>
        <div style={msg}>Ready to start the drive to the site?</div>
        <button style={primary('#166534', '#dcfce7')} onClick={() => guardDrive('site')}>Yes — start driving (clock on)</button>
        <button style={ghost} onClick={() => { setWizard(null); setStep('drive'); }}>Not yet</button>
      </div>
    );
  }
  if (wizard === 'bench') {
    return (
      <div style={wrap}>
        <div style={head}>Bench</div>
        <div style={msg}>Need directions to the bench?</div>
        {!benchUrl && <div style={sub}>(No bench location set on your profile — directions unavailable.)</div>}
        <button style={primary('#166534', '#dcfce7')} onClick={() => startBench(true)} disabled={!benchUrl}>Yes — navigate + start clock</button>
        <button style={ghost} onClick={() => startBench(false)}>No — just start bench work</button>
        <button style={ghost} onClick={() => setWizard(null)}>Cancel</button>
      </div>
    );
  }

  if (wizard === 'verify') {
    return (
      <div style={wrap}>
        <div style={head}>Hold on</div>
        <div style={msg}>⚠️ Material not checked out</div>
        <div style={sub}>This job has material that was never checked out. Scan the order QR on the material to check it out before you drive.</div>
        <button style={primary('#166534', '#dcfce7')} onClick={async () => { const ok = await recheckMaterial(); if (ok) { const k = pendingDrive; setWizard(null); setPendingDrive(null); runDrive(k); } else { alert('Still not checked out — scan the order QR on the material first.'); } }}>🔄 I scanned it — re-check</button>
        <button style={ghost} onClick={() => { const k = pendingDrive; setWizard(null); setPendingDrive(null); runDrive(k); }}>Drive anyway</button>
        <button style={ghost} onClick={() => { setWizard(null); setPendingDrive(null); }}>Cancel</button>
      </div>
    );
  }

  // ── by step ──
  let body;
  if (!step) {
    body = (<>
      <div style={msg}>Ready to start</div>
      <div style={sub}>Pick how you're starting this job.</div>
      <button style={primary()} onClick={() => setWizard('material')}>▶ Start Install</button>
      <button style={ghost} onClick={() => setWizard('bench')}>🧰 Place Equipment on Bench</button>
    </>);
  } else if (step === 'pickup') {
    body = (<>
      <div style={msg}>Pick up the material first</div>
      <div style={sub}>Scan the order QR on the material to check it out, then continue.</div>
      <button style={primary('#166534', '#dcfce7')} onClick={() => setWizard('drive')}>✓ I have the material</button>
    </>);
  } else if (step === 'drive' && openType !== 'drive') {
    body = (<>
      <div style={msg}>Ready to roll</div>
      <div style={sub}>Tap when you're leaving — the drive clock starts.</div>
      <button style={primary()} onClick={() => guardDrive('site')}>🚗 Start Drive to Site</button>
    </>);
  } else if (step === 'drive' && openType === 'drive') {
    body = (<>
      <div style={msg}>🚗 Driving to site — clock running</div>
      <div style={sub}>Auto-stops when you reach the site. Or tap below.</div>
      {siteUrl && <button style={ghost} onClick={() => window.open(siteUrl, '_blank')}>🧭 Open Maps</button>}
      <button style={primary('#0f766e', '#ccfbf1')} onClick={arrivedAtSite}>📍 I've Arrived</button>
    </>);
  } else if (step === 'arrived') {
    body = (<>
      <div style={msg}>📍 At the site</div>
      <div style={sub}>Ready to begin the install?</div>
      <button style={primary('#166534', '#dcfce7')} onClick={begin}>✅ Begin Install (opens JHA)</button>
    </>);
  } else if (step === 'installing') {
    body = (<>
      <div style={msg}>🔧 Install in progress</div>
      <div style={sub}>When the physical install is done, mark it complete.</div>
      <button style={primary('#166534', '#dcfce7')} onClick={() => onFieldComplete && onFieldComplete()}>✅ Field Install Complete</button>
      <button style={ghost} onClick={() => onBeginInstall()}>Open JHA</button>
    </>);
  } else if (step === 'bench' && openType === 'drive') {
    body = (<>
      <div style={msg}>🚗 Driving to bench — clock running</div>
      {benchUrl && <button style={ghost} onClick={() => window.open(benchUrl, '_blank')}>🧭 Open Maps</button>}
      <button style={primary('#0f766e', '#ccfbf1')} onClick={arrivedAtBench}>📍 Arrived at Bench</button>
    </>);
  } else if (step === 'bench') {
    body = (<>
      <div style={msg}>🧰 Bench work in progress</div>
      <div style={sub}>When the bench work is done, head to the site.</div>
      <button style={primary()} onClick={() => guardDrive('bench')}>✅ Done — Drive to Site</button>
    </>);
  }

  return (
    <div style={wrap}>
      <div style={head}>Field Flow</div>
      {body}
      {step && <button style={{ ...ghost, marginTop: 12, fontSize: 11, opacity: 0.6 }} onClick={() => { stopTimer(); setStep(null); }}>↺ Reset flow</button>}
    </div>
  );
}
